import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import '../assets/BulkExportModal.css';

const BulkExportModal = ({ 
  isOpen, 
  onClose, 
  tutors, 
  tutees, 
  endorsedTutors = [], 
  month, 
  year, 
  endorsementContext = false,
  onBulkComplete 
}) => {
  const [exportType, setExportType] = useState('tutor');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportError, setExportError] = useState('');
  const [exportComplete, setExportComplete] = useState(false);
  const [exportResults, setExportResults] = useState(null);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2019 + 6 }, (_, i) => 2019 + i);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedRecipients([]);
      setSearchTerm('');
      setIsExporting(false);
      setExportProgress(0);
      setExportError('');
      setExportComplete(false);
      setExportResults(null);
      
      // If in endorsement context, set month/year from props and default to tutor type
      if (endorsementContext && month && year) {
        setSelectedMonth(month);
        setSelectedYear(year);
        setExportType('tutor');
      }
    }
  }, [isOpen, endorsementContext, month, year]);

  if (!isOpen) return null;

  const recipients = endorsementContext && exportType === 'tutor' 
    ? endorsedTutors 
    : (exportType === 'tutor' ? tutors : tutees);

  const filteredRecipients = recipients.filter(r => 
    !r.isArchived && (
      r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.email?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleSelectAll = () => {
    if (selectedRecipients.length === filteredRecipients.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(filteredRecipients.map(r => r._id));
    }
  };

  const handleToggleRecipient = (recipientId) => {
    if (selectedRecipients.includes(recipientId)) {
      setSelectedRecipients(selectedRecipients.filter(id => id !== recipientId));
    } else {
      setSelectedRecipients([...selectedRecipients, recipientId]);
    }
  };

  // Calculate total amount for selected tutors (only in endorsement context)
  const getSelectedTotalAmount = () => {
    if (!endorsementContext || exportType !== 'tutor') return 0;
    
    return endorsedTutors
      .filter(tutor => selectedRecipients.includes(tutor._id))
      .reduce((total, tutor) => total + (tutor.totalAmount || 0), 0);
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  const handleExport = async () => {
    if (selectedRecipients.length === 0) {
      setExportError('Please select at least one recipient');
      return;
    }

    setIsExporting(true);
    setExportProgress(0);
    setExportError('');
    setExportComplete(false);

    try {
      // Simulate progress (since we can't track actual backend progress easily)
      const progressInterval = setInterval(() => {
        setExportProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + 15;
        });
      }, 200);

      const response = await adminApi.bulkExportInvoices({
        invoiceType: exportType,
        recipientIds: selectedRecipients,
        month: selectedMonth,
        year: selectedYear
      });

      clearInterval(progressInterval);
      setExportProgress(100);

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/zip' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices_${exportType}_${selectedMonth}_${selectedYear}.zip`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Set results for display
      setExportResults({
        successful: selectedRecipients.length,
        total: selectedRecipients.length,
        downloadName: `invoices_${exportType}_${selectedMonth}_${selectedYear}.zip`
      });
      
      setExportComplete(true);

      // Call completion callback to refresh endorsement data
      if (onBulkComplete) {
        onBulkComplete();
      }

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error exporting invoices:', error);
      setExportError(
        error.response?.data?.message || 
        'Failed to export invoices. Please try again.'
      );
      setExportProgress(0);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRetry = () => {
    setExportError('');
    setExportProgress(0);
    handleExport();
  };

  return (
    <div className="bulk-export-modal-overlay" onClick={onClose}>
      <div className="bulk-export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {endorsementContext 
              ? `Bulk Generate Invoices - ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`
              : 'Bulk Export Invoices'
            }
          </h2>
          <button className="close-btn" onClick={onClose} disabled={isExporting}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Export Type Selection */}
          {!endorsementContext && (
            <div className="form-group">
              <label>Invoice Type:</label>
              <div className="type-selector">
                <button
                  className={exportType === 'tutor' ? 'active' : ''}
                  onClick={() => {
                    setExportType('tutor');
                    setSelectedRecipients([]);
                  }}
                  disabled={isExporting}
                >
                  Tutor Invoices
                </button>
                <button
                  className={exportType === 'tutee' ? 'active' : ''}
                  onClick={() => {
                    setExportType('tutee');
                    setSelectedRecipients([]);
                  }}
                  disabled={isExporting}
                >
                  Tutee Invoices
                </button>
              </div>
            </div>
          )}

          {/* Endorsement Context Info */}
          {endorsementContext && (
            <div className="endorsement-info">
              <p>Generating invoices for endorsed tutors only</p>
              <p className="total-amount">
                Total Amount: <strong>{formatCurrency(getSelectedTotalAmount())}</strong>
              </p>
            </div>
          )}

          {/* Period Selection */}
          {!endorsementContext && (
            <div className="form-row">
              <div className="form-group">
                <label>Month:</label>
                <select 
                  value={selectedMonth} 
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  disabled={isExporting}
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Year:</label>
                <select 
                  value={selectedYear} 
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  disabled={isExporting}
                >
                  {years.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Recipient Selection */}
          <div className="form-group">
            <label>
              {endorsementContext 
                ? `Select Endorsed Tutors:`
                : `Select ${exportType === 'tutor' ? 'Tutors' : 'Tutees'}:`
              }
              <span className="selection-count">
                ({selectedRecipients.length} selected)
              </span>
              {endorsementContext && selectedRecipients.length > 0 && (
                <span className="total-amount-inline">
                  - {formatCurrency(getSelectedTotalAmount())}
                </span>
              )}
            </label>

            <div className="search-box">
              <input
                type="text"
                placeholder={`Search ${exportType}s...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={isExporting}
              />
            </div>

            <div className="select-all-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedRecipients.length === filteredRecipients.length && filteredRecipients.length > 0}
                  onChange={handleSelectAll}
                  disabled={isExporting}
                />
                <span>Select All ({filteredRecipients.length})</span>
              </label>
            </div>

            <div className="recipients-list">
              {filteredRecipients.length === 0 ? (
                <div className="no-recipients">
                  No {exportType}s found
                </div>
              ) : (
                filteredRecipients.map(recipient => (
                  <label key={recipient._id} className="recipient-item">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.includes(recipient._id)}
                      onChange={() => handleToggleRecipient(recipient._id)}
                      disabled={isExporting}
                    />
                    <div className="recipient-info">
                      <div className="recipient-name">{recipient.name}</div>
                      {recipient.email && (
                        <div className="recipient-email">{recipient.email}</div>
                      )}
                      {endorsementContext && recipient.totalAmount && (
                        <div className="recipient-amount">
                          {recipient.lessonCount} lessons - {formatCurrency(recipient.totalAmount)}
                        </div>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Progress Bar */}
          {isExporting && (
            <div className="progress-section">
              <div className="progress-label">
                Generating {selectedRecipients.length} invoices... {exportProgress}%
              </div>
              <div className="progress-details">
                <p>Processing {exportType} invoices for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}</p>
                {endorsementContext && (
                  <p>Total Amount: {formatCurrency(getSelectedTotalAmount())}</p>
                )}
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${exportProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {exportComplete && exportResults && (
            <div className="success-message">
              <div className="success-header">
                ✓ Bulk Export Completed Successfully!
              </div>
              <div className="success-details">
                <p>Generated {exportResults.successful} of {exportResults.total} invoices</p>
                <p>Download: {exportResults.downloadName}</p>
                {endorsementContext && (
                  <p>Total Amount: {formatCurrency(getSelectedTotalAmount())}</p>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {exportError && (
            <div className="error-message">
              <div className="error-text">✗ {exportError}</div>
              <button className="retry-btn" onClick={handleRetry}>
                Retry
              </button>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="cancel-btn" 
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </button>
          <button 
            className="export-btn" 
            onClick={handleExport}
            disabled={isExporting || selectedRecipients.length === 0}
          >
            {isExporting ? (
              <>
                <span className="btn-spinner"></span>
                Exporting...
              </>
            ) : (
              <>
                <span className="btn-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM10 4h4v2h-4V4zm10 16H4V8h16v12z"/>
                  </svg>
                </span>
                Export {selectedRecipients.length} Invoice{selectedRecipients.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkExportModal;
